import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Package, ArrowClockwise, CaretDown, CaretRight, Warehouse, CurrencyDollar, Sliders, Plus, Minus, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import TiendaNubeSyncPanels from "@/components/TiendaNubeSyncPanels";
import StockOrphansBanner from "@/components/StockOrphansBanner";
import { useSyncStockTN } from "@/hooks/useSyncStockTN";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Topbar from "@/components/layout/Topbar";
import api from "@/lib/api";
import NumberInput from "@/components/ui/NumberInput";

const fmtKg = (n) => `${new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0)} kg`;
const fmtMoney = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 }).format(Number(n) || 0);

export default function StockPage() {
  const [data, setData] = useState({ productos: [], total_productos: 0, total_kilos: 0, total_valor: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [ajusteOpen, setAjusteOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);   // { file, resumen }
  const [importing, setImporting] = useState(false);
  // Sync manual de stock system → TN (movido de TiendaNubePage). Vive acá
  // porque el operador de Stock es quien mejor sabe cuándo forzar el push.
  const { syncing: syncingTN, start: startSyncTN, progressLabel: syncTNLabel } = useSyncStockTN();
  const handleSyncStockTN = useCallback(() => {
    if (!window.confirm("¿Empujar el stock actual del sistema a Tienda Nube? Se matchea por código de producto (SKU). Los productos con 'Stock infinito TN' tildado se mandan como 9999. Corre en segundo plano: podés seguir usando el sistema.")) return;
    startSyncTN();
  }, [startSyncTN]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock", { params: { q: q || undefined } });
      setData(data);
    } catch (e) {
      toast.error("No se pudo cargar el stock");
    } finally {
      setLoading(false);
    }
  }, [q]);

  const handleDescargarExcel = useCallback(async () => {
    try {
      const res = await api.get("/stock/export-excel", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const now = new Date();
      a.href = url;
      a.download = `stock_ajuste_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado. Editá 'Kilos nuevos' y volvé a subirlo.");
    } catch (err) {
      toast.error("No se pudo descargar el Excel");
    }
  }, []);

  const handleImportExcel = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const { data } = await api.post("/stock/import-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview({ file, resumen: data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo leer el Excel");
    }
  }, []);

  const confirmarImport = useCallback(async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview.file);
      fd.append("dry_run", "false");
      const { data } = await api.post("/stock/import-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Ajuste aplicado: ${data.ingresos} ingreso(s), ${data.bajas} baja(s)`);
      setImportPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar el ajuste");
    } finally {
      setImporting(false);
    }
  }, [importPreview, load]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  return (
    <>
      <Topbar title="Stock" subtitle="Existencias consolidadas por producto · trazadas por lotes activos" />
      <div className="p-4 space-y-4" data-testid="stock-page">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por descripción o código..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              className="pl-9"
              data-testid="stock-search"
            />
          </div>
          <Button onClick={load} variant="outline" className="gap-2" data-testid="stock-refresh">
            <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} /> Actualizar
          </Button>
          <Button
            onClick={handleSyncStockTN}
            disabled={syncingTN}
            variant="outline"
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="stock-sync-tn-btn"
            title="Empujar stock actual del sistema a Tienda Nube (match por SKU)"
          >
            <ArrowClockwise size={16} className={syncingTN ? "animate-spin" : ""} />
            {syncingTN ? (syncTNLabel || "Empujando...") : "Sincronizar stock → TN"}
          </Button>

          <Button
            onClick={handleDescargarExcel}
            variant="outline"
            className="gap-2"
            data-testid="stock-export-btn"
            title="Descargar Excel para editar el stock"
          >
            <DownloadSimple size={16} /> Excel
          </Button>
          <Button
            onClick={() => document.getElementById("stock-import-file").click()}
            variant="outline"
            className="gap-2"
            data-testid="stock-import-btn"
            title="Subir Excel modificado"
          >
            <UploadSimple size={16} /> Importar
          </Button>
          <input
            id="stock-import-file"
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={handleImportExcel}
            data-testid="stock-import-file"
          />
          <Button
            onClick={() => setAjusteOpen(true)}
            className="gap-2 bg-[#921A0E] hover:bg-[#7d1509] text-white"
            data-testid="stock-ajustar-btn"
          >
            <Sliders size={16} /> Ajustar Stock
          </Button>
        </div>

        {/* Alerta de lotes huérfanos (productos que están en Stock pero no en Productos y Precios) */}
        <StockOrphansBanner onChanged={load} />

        {/* Config Auto-sync + Alerta SKU + Historial de sync (compartido con /tienda-nube) */}
        <TiendaNubeSyncPanels testidPrefix="stock" />

        <div className="grid grid-cols-2 gap-3">
          <StatCard icon={Package} label="Productos con stock" value={data.total_productos} testid="stat-productos" />
          <StatCard icon={Warehouse} label="Kilos totales" value={fmtKg(data.total_kilos)} testid="stat-kilos" />
        </div>

        <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-neutral-100 text-[11px] uppercase font-mono text-neutral-600 border-b">
            <div className="col-span-8">Producto</div>
            <div className="col-span-3 text-right">Disponible</div>
            <div className="col-span-1 text-right">Lotes</div>
          </div>
          {data.productos.length === 0 ? (
            <div className="text-center py-16 text-neutral-400 text-sm">Sin productos con stock disponible.</div>
          ) : data.productos.map((p) => {
            const key = p.producto_normalizado;
            const open = expanded.has(key);
            const negativo = Number(p.kilos_disponibles || 0) < 0;
            return (
              <div key={key} className={`border-b border-neutral-100 last:border-b-0 ${negativo ? "bg-red-50/60" : ""}`} data-testid={`stock-row-${key}`}>
                <button
                  className={`w-full grid grid-cols-12 gap-3 px-4 py-3 items-center text-left ${negativo ? "hover:bg-red-100/60" : "hover:bg-neutral-50"}`}
                  onClick={() => toggle(key)}
                >
                  <div className="col-span-8 flex items-center gap-2 min-w-0">
                    {open ? <CaretDown size={14} className="text-neutral-500" /> : <CaretRight size={14} className="text-neutral-500" />}
                    <Package size={16} className={negativo ? "text-red-700" : "text-[#921A0E]"} />
                    <div className="min-w-0">
                      <div className={`font-semibold text-sm truncate ${negativo ? "text-red-800" : ""}`}>
                        {p.descripcion}
                        {negativo && (
                          <Badge variant="outline" className="ml-2 text-[10px] bg-red-100 text-red-800 border-red-300 align-middle" title="Se vendió más de lo que había — revisar compras o transformaciones pendientes">
                            SIN STOCK
                          </Badge>
                        )}
                      </div>
                      {p.codigo && <div className="text-[10px] font-mono text-neutral-500">{p.codigo}</div>}
                    </div>
                  </div>
                  <div className={`col-span-3 text-right font-mono font-bold text-sm ${negativo ? "text-red-700" : ""}`}>{fmtKg(p.kilos_disponibles)}</div>
                  <div className="col-span-1 text-right">
                    <Badge variant="outline" className="text-[11px] font-mono">{p.lotes.length}</Badge>
                  </div>
                </button>
                {open && (
                  <div className="bg-neutral-50/50 px-4 py-2 border-t border-neutral-100 space-y-3">
                    <div className="overflow-x-auto">
                      <div className="text-[10px] font-mono uppercase text-neutral-500 mb-1">Lotes activos ({p.lotes.length})</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-neutral-500 font-mono uppercase text-[10px]">
                            <th className="text-left py-1">Lote</th>
                            <th className="text-left py-1">Elaboración</th>
                            <th className="text-left py-1">Vencimiento</th>
                            <th className="text-left py-1">Tipo</th>
                            <th className="text-right py-1">Entrada</th>
                            <th className="text-right py-1">Disponible</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.lotes.map((l) => (
                            <tr key={l.id} className="border-t border-neutral-100" data-testid={`stock-lote-${l.numero}`}>
                              <td className="py-1 font-mono font-bold text-[#921A0E]">{l.numero}</td>
                              <td className="py-1 font-mono">{l.fecha_elaboracion}</td>
                              <td className="py-1 font-mono text-neutral-500">{l.fecha_vencimiento || "—"}</td>
                              <td className="py-1">
                                <Badge variant="outline" className={`text-[10px] ${l.tipo_origen === "transformacion" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                                  {l.tipo_origen === "transformacion" ? "Despiece" : "Recepción"}
                                </Badge>
                              </td>
                              <td className="py-1 text-right font-mono">{fmtKg(l.kilos_entrada)}</td>
                              <td className="py-1 text-right font-mono font-bold">{fmtKg(l.kilos_disponibles)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <MovimientosProducto productoId={p.producto_id} productoNorm={p.producto_normalizado} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Diálogo de Ajuste de Stock */}
      <AjusteStockDialog
        open={ajusteOpen}
        onOpenChange={setAjusteOpen}
        productosEnStock={data.productos || []}
        onDone={load}
      />

      {/* Diálogo de preview de import Excel */}
      <Dialog open={!!importPreview} onOpenChange={(o) => !o && setImportPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>Previsualización de ajuste masivo</DialogTitle>
          </DialogHeader>
          {importPreview?.resumen && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Total</div>
                  <div className="text-lg font-bold">{importPreview.resumen.procesados}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-emerald-700">Ingresos</div>
                  <div className="text-lg font-bold text-emerald-700">{importPreview.resumen.ingresos}</div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-red-700">Bajas</div>
                  <div className="text-lg font-bold text-red-700">{importPreview.resumen.bajas}</div>
                </div>
                <div className="border border-neutral-200 rounded p-2">
                  <div className="text-[10px] uppercase font-mono text-neutral-500">Sin cambios</div>
                  <div className="text-lg font-bold text-neutral-600">{importPreview.resumen.sin_cambios}</div>
                </div>
              </div>
              {importPreview.resumen.errores?.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded p-2 text-[12px] text-amber-800">
                  <b>{importPreview.resumen.errores.length} error(es):</b>
                  <ul className="list-disc list-inside">
                    {importPreview.resumen.errores.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              <div className="max-h-64 overflow-y-auto border border-neutral-200 rounded">
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50 sticky top-0 text-[10px] uppercase font-mono text-neutral-600">
                    <tr>
                      <th className="text-left px-2 py-1">Producto</th>
                      <th className="text-right px-2 py-1">Actual</th>
                      <th className="text-right px-2 py-1">Nuevo</th>
                      <th className="text-right px-2 py-1">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.resumen.detalle_ingresos?.map((r, i) => (
                      <tr key={`i-${i}`} className="border-t border-neutral-100">
                        <td className="px-2 py-1">{r.producto}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_actuales}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_nuevos}</td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-700 font-bold">+{r.kilos}</td>
                      </tr>
                    ))}
                    {importPreview.resumen.detalle_bajas?.map((r, i) => (
                      <tr key={`b-${i}`} className="border-t border-neutral-100">
                        <td className="px-2 py-1">{r.producto}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_actuales}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.kilos_nuevos}</td>
                        <td className="px-2 py-1 text-right font-mono text-red-700 font-bold">−{r.kilos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportPreview(null)} disabled={importing}>Cancelar</Button>
            <Button
              onClick={confirmarImport}
              disabled={importing || !(importPreview?.resumen?.procesados > 0)}
              className="bg-[#921A0E] hover:bg-[#7d1509] text-white"
              data-testid="import-confirmar-btn"
            >
              {importing ? "Aplicando…" : `Aplicar ${importPreview?.resumen?.procesados || 0} ajuste(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AjusteStockDialog({ open, onOpenChange, productosEnStock, onDone }) {
  const [tipo, setTipo] = useState("ingreso"); // ingreso | baja
  const [productoNombre, setProductoNombre] = useState("");
  const [productoId, setProductoId] = useState(null);
  const [kilos, setKilos] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fechaElab, setFechaElab] = useState(() => new Date().toISOString().slice(0, 10));
  const [fechaVenc, setFechaVenc] = useState("");
  const [loteNumero, setLoteNumero] = useState("");
  const [costoKg, setCostoKg] = useState("");
  const [busyProds, setBusyProds] = useState(false);
  const [productosCatalog, setProductosCatalog] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cargar catálogo completo de productos para sugerencias (solo la primera vez)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setBusyProds(true);
      try {
        const { data } = await api.get("/productos", { params: { limit: 500 } });
        const items = Array.isArray(data) ? data : (data.items || []);
        if (!cancelled) setProductosCatalog(items);
      } catch (_) { /* silencioso */ }
      finally { if (!cancelled) setBusyProds(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) {
      setTipo("ingreso");
      setProductoNombre(""); setProductoId(null);
      setKilos(""); setMotivo("");
      setFechaElab(new Date().toISOString().slice(0, 10));
      setFechaVenc(""); setLoteNumero(""); setCostoKg("");
      setShowSuggest(false);
    }
  }, [open]);

  const sugerencias = useMemo(() => {
    const q = productoNombre.trim().toLowerCase();
    // Fusionar catálogo + productos actualmente en stock
    const base = new Map();
    for (const p of productosCatalog) {
      base.set((p.nombre || "").toLowerCase(), {
        id: p.id, nombre: p.nombre, codigo: p.codigo,
        precio_actual: p.precio_actual, ultimo_costo: p.ultimo_costo,
      });
    }
    for (const p of productosEnStock) {
      const key = (p.descripcion || "").toLowerCase();
      if (!base.has(key)) {
        base.set(key, { id: p.producto_id, nombre: p.descripcion, codigo: p.codigo });
      }
    }
    const arr = Array.from(base.values()).filter((p) => (p.nombre || "").length > 0);
    if (!q) return arr.slice(0, 20);
    return arr.filter((p) => (p.nombre || "").toLowerCase().includes(q)).slice(0, 20);
  }, [productoNombre, productosCatalog, productosEnStock]);

  const pickProducto = (p) => {
    setProductoNombre(p.nombre);
    setProductoId(p.id || null);
    if (tipo === "ingreso" && p.ultimo_costo && !costoKg) {
      setCostoKg(String(p.ultimo_costo));
    }
    setShowSuggest(false);
  };

  const submit = async () => {
    if (!productoNombre.trim()) { toast.error("Elegí un producto"); return; }
    const kg = parseFloat(kilos);
    if (!(kg > 0)) { toast.error("Ingresá una cantidad válida en kilos"); return; }
    setSaving(true);
    try {
      const payload = {
        tipo, kilos: kg, motivo: motivo.trim() || null,
        producto_id: productoId || undefined,
        producto_nombre: productoNombre.trim(),
      };
      if (tipo === "ingreso") {
        payload.fecha_elaboracion = fechaElab || undefined;
        payload.fecha_vencimiento = fechaVenc || undefined;
        payload.lote_numero = loteNumero.trim() || undefined;
        payload.costo_por_kg = parseFloat(costoKg) || 0;
      }
      const { data: res } = await api.post("/stock/ajuste", payload);
      if (tipo === "ingreso") {
        toast.success(`Ingreso registrado · Lote ${res?.lote?.numero || ""} · ${kg} kg`);
      } else {
        const dados = res?.kilos_dados_de_baja || 0;
        const faltantes = res?.kilos_faltantes || 0;
        if (faltantes > 0) toast.warning(`Baja parcial: ${dados} kg dados de baja, faltaron ${faltantes} kg (sin stock)`);
        else toast.success(`Baja registrada · ${dados} kg`);
      }
      onDone && onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo aplicar el ajuste");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ajuste-stock-dialog">
        <DialogHeader>
          <DialogTitle>Ajustar Stock</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo de ajuste */}
          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">
              Tipo de ajuste
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTipo("ingreso")}
                className={`flex items-center justify-center gap-2 h-11 rounded-sm border text-sm font-medium ${
                  tipo === "ingreso"
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-neutral-300 hover:bg-neutral-50"
                }`}
                data-testid="ajuste-tipo-ingreso"
              >
                <Plus size={16} weight="bold" /> Ingreso · Stock inicial
              </button>
              <button
                type="button"
                onClick={() => setTipo("baja")}
                className={`flex items-center justify-center gap-2 h-11 rounded-sm border text-sm font-medium ${
                  tipo === "baja"
                    ? "bg-red-700 border-red-700 text-white"
                    : "bg-white border-neutral-300 hover:bg-neutral-50"
                }`}
                data-testid="ajuste-tipo-baja"
              >
                <Minus size={16} weight="bold" /> Baja · Merma / Descarte
              </button>
            </div>
          </div>

          {/* Producto */}
          <div className="relative">
            <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">
              Producto
            </Label>
            <Input
              value={productoNombre}
              onChange={(e) => { setProductoNombre(e.target.value); setProductoId(null); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              placeholder={busyProds ? "Cargando productos…" : "Escribí para buscar…"}
              className="h-10"
              data-testid="ajuste-producto"
            />
            {showSuggest && sugerencias.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-sm shadow-lg max-h-60 overflow-y-auto">
                {sugerencias.map((p, i) => (
                  <button
                    key={`${p.id || "x"}-${i}`}
                    type="button"
                    onClick={() => pickProducto(p)}
                    className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                    data-testid={`ajuste-producto-opt-${i}`}
                  >
                    <div className="text-sm">{p.nombre}</div>
                    {p.codigo && <div className="text-[10px] font-mono text-neutral-500">{p.codigo}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Kilos + Motivo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">Kilos</Label>
              <NumberInput
                step="0.001"
                min="0"
                value={kilos}
                onChange={(e) => setKilos(e.target.value)}
                placeholder="0"
                className="h-10 font-mono text-right"
                data-testid="ajuste-kilos"
              />
            </div>
            {tipo === "ingreso" && (
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">Costo por kg (opc.)</Label>
                <NumberInput
                  step="0.01"
                  min="0"
                  value={costoKg}
                  onChange={(e) => setCostoKg(e.target.value)}
                  placeholder="$"
                  className="h-10 font-mono text-right"
                  data-testid="ajuste-costo"
                />
              </div>
            )}
          </div>

          {tipo === "ingreso" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">Fecha de elaboración</Label>
                <Input type="date" value={fechaElab} onChange={(e) => setFechaElab(e.target.value)} className="h-10" data-testid="ajuste-fecha-elab" />
              </div>
              <div>
                <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">Vencimiento (opc.)</Label>
                <Input type="date" value={fechaVenc} onChange={(e) => setFechaVenc(e.target.value)} className="h-10" data-testid="ajuste-fecha-venc" />
              </div>
            </div>
          )}

          {tipo === "ingreso" && (
            <div>
              <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">Nº de lote (opcional · se autogenera)</Label>
              <Input
                value={loteNumero}
                onChange={(e) => setLoteNumero(e.target.value)}
                placeholder="Dejar vacío para autonumerar"
                className="h-10 font-mono"
                data-testid="ajuste-lote-numero"
              />
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase font-mono text-neutral-500 tracking-widest mb-1 block">
              Motivo {tipo === "baja" && <span className="text-red-600">*</span>}
            </Label>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={tipo === "ingreso" ? "Stock inicial, corrección de inventario…" : "Producto en mal estado, error de despacho, robo…"}
              rows={3}
              data-testid="ajuste-motivo"
            />
          </div>

          {tipo === "baja" && (
            <div className="text-[11px] font-mono text-neutral-500 border-l-2 border-amber-400 pl-2 bg-amber-50/40 py-1">
              La baja descuenta del lote más viejo primero (FIFO). Si no alcanza stock, queda un movimiento con faltante.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="ajuste-cancel">
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className={`${tipo === "ingreso" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-700 hover:bg-red-800"} text-white`}
            data-testid="ajuste-submit"
          >
            {saving ? "Guardando…" : (tipo === "ingreso" ? "Registrar ingreso" : "Dar de baja")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MovimientosProducto({ productoId, productoNorm }) {
  const [movs, setMovs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [limit, setLimit] = useState(15);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/stock/producto-movimientos", {
      params: {
        producto_id: productoId || undefined,
        producto_norm: productoNorm || undefined,
        limit,
        fecha_desde: desde || undefined,
        fecha_hasta: hasta || undefined,
      },
    })
      .then(({ data }) => { if (!cancel) setMovs(data?.movimientos || []); })
      .catch(() => { if (!cancel) setMovs([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [productoId, productoNorm, desde, hasta, limit]);

  const tipoLabel = (t) => ({
    venta: "Venta", ajuste_baja: "Ajuste (baja)", ajuste_ingreso: "Ajuste (ingreso)",
    transformacion: "Transformación", receta_auto: "Receta (auto)", receta: "Receta",
    compra_directa: "Compra directa", ajuste_manual: "Ajuste manual",
    inventario: "Inventario", recepcion: "Recepción", ingreso: "Ingreso",
    produccion: "Producción",
  }[t] || t);

  return (
    <div className="overflow-x-auto pt-2" data-testid="stock-movimientos-producto">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase text-neutral-500">
          Movimientos {desde || hasta ? "en rango" : `(últimos ${limit})`}
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          <span className="text-neutral-500">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                 className="border border-neutral-200 rounded px-1 py-0.5 font-mono text-[11px]" data-testid="movs-fecha-desde" />
          <span className="text-neutral-500">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                 className="border border-neutral-200 rounded px-1 py-0.5 font-mono text-[11px]" data-testid="movs-fecha-hasta" />
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                  className="border border-neutral-200 rounded px-1 py-0.5 font-mono text-[11px]" data-testid="movs-limit">
            <option value={15}>15</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
          </select>
          {(desde || hasta) && (
            <button className="text-[11px] text-[#921A0E] hover:underline"
                    onClick={() => { setDesde(""); setHasta(""); }} data-testid="movs-clear">
              Limpiar
            </button>
          )}
        </div>
      </div>
      {loading && <div className="text-xs text-neutral-400 py-2">Cargando…</div>}
      {!loading && (movs?.length ?? 0) === 0 && (
        <div className="text-xs text-neutral-400 py-2">Sin movimientos en el período.</div>
      )}
      {!loading && (movs?.length ?? 0) > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-neutral-500 font-mono uppercase text-[10px]">
              <th className="text-left py-1">Fecha</th>
              <th className="text-left py-1">Módulo</th>
              <th className="text-left py-1">Usuario</th>
              <th className="text-left py-1">Lote</th>
              <th className="text-left py-1">Referencia</th>
              <th className="text-right py-1">Kilos</th>
              <th className="text-right py-1">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {movs.map((m, i) => {
              const isEntrada = m.direccion === "entrada";
              const isSalida = m.direccion === "salida";
              const cls = isEntrada ? "text-emerald-700" : isSalida ? "text-red-700" : "text-neutral-500";
              const saldo = Number(m.saldo_kilos ?? 0);
              const saldoCls = saldo < 0 ? "text-red-700" : "text-neutral-700";
              return (
                <tr key={i} className="border-t border-neutral-100" data-testid={`stock-mov-${i}`}>
                  <td className="py-1 font-mono text-neutral-600">{(m.fecha || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-1">
                    <Badge variant="outline" className={`text-[10px] ${isEntrada ? "bg-emerald-50 text-emerald-700 border-emerald-200" : isSalida ? "bg-red-50 text-red-700 border-red-200" : "bg-neutral-50 text-neutral-600 border-neutral-200"}`}>
                      {m.modulo || tipoLabel(m.tipo)}
                    </Badge>
                    {m.faltante && <Badge variant="outline" className="ml-1 text-[9px] bg-amber-50 text-amber-700 border-amber-200">faltante</Badge>}
                  </td>
                  <td className="py-1 font-mono text-[11px] text-neutral-600" data-testid={`stock-mov-user-${i}`}>
                    {m.usuario || <span className="text-neutral-300 italic">—</span>}
                  </td>
                  <td className="py-1 font-mono text-[11px]">{m.lote_numero || "—"}</td>
                  <td className="py-1 text-[11px] text-neutral-600 truncate max-w-[240px]" title={m.referencia}>{m.referencia || "—"}</td>
                  <td className={`py-1 text-right font-mono font-semibold ${cls}`}>
                    {isEntrada ? "+" : isSalida ? "−" : ""}{fmtKg(m.kilos).replace(/kg/, "").trim()} kg
                  </td>
                  <td className={`py-1 text-right font-mono ${saldoCls}`} data-testid={`stock-mov-saldo-${i}`}
                      title="Kilos totales del producto justo después de este movimiento">
                    {fmtKg(saldo).replace(/kg/, "").trim()} kg
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-3 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" className="text-[#921A0E]" />
        <div className="text-xs uppercase font-mono text-neutral-500">{label}</div>
      </div>
      <div className="text-xl font-bold text-neutral-800">{value}</div>
    </div>
  );
}
